import {
  apiRequestPost,
  apiRequestGet,
  apiRequestDelete,
  apiRequestPatch,
} from 'flavours/glitch/api';
import type {
  ApiAccountJSON,
  ApiFamiliarFollowersJSON,
} from 'flavours/glitch/api_types/accounts';
import type { ApiRelationshipJSON } from 'flavours/glitch/api_types/relationships';
import type {
  ApiFeaturedTagJSON,
  ApiHashtagJSON,
} from 'flavours/glitch/api_types/tags';

import type {
  ApiProfileJSON,
  ApiProfileUpdateParams,
} from '../api_types/profile';
import { mastodonApiVersion } from 'flavours/glitch/initial_state';

export const apiGetAccounts = (ids: string[]) =>
  apiRequestGet<ApiAccountJSON[]>('v1/accounts', {
    id: ids,
  });

export const apiSubmitAccountNote = (id: string, value: string) =>
  apiRequestPost<ApiRelationshipJSON>(`v1/accounts/${id}/note`, {
    comment: value,
  });

export const apiFollowAccount = (
  id: string,
  params?: {
    reblogs: boolean;
  },
) =>
  apiRequestPost<ApiRelationshipJSON>(`v1/accounts/${id}/follow`, {
    ...params,
  });

export const apiUnfollowAccount = (id: string) =>
  apiRequestPost<ApiRelationshipJSON>(`v1/accounts/${id}/unfollow`);

export const apiRemoveAccountFromFollowers = (id: string) =>
  apiRequestPost<ApiRelationshipJSON>(
    `v1/accounts/${id}/remove_from_followers`,
  );

export const apiGetFeaturedTags = (id: string) =>
  apiRequestGet<ApiHashtagJSON[]>(`v1/accounts/${id}/featured_tags`);

export const apiGetCurrentFeaturedTags = () =>
  apiRequestGet<ApiFeaturedTagJSON[]>(`v1/featured_tags`);

export const apiPostFeaturedTag = (name: string) =>
  apiRequestPost<ApiFeaturedTagJSON>('v1/featured_tags', { name });

export const apiDeleteFeaturedTag = (id: string) =>
  apiRequestDelete(`v1/featured_tags/${id}`);

export const apiGetTagSuggestions = () =>
  apiRequestGet<ApiHashtagJSON[]>('v1/featured_tags/suggestions');

export const apiGetEndorsedAccounts = (id: string) =>
  apiRequestGet<ApiAccountJSON>(`v1/accounts/${id}/endorsements`);

export const apiGetFamiliarFollowers = (id: string) =>
  apiRequestGet<ApiFamiliarFollowersJSON>('v1/accounts/familiar_followers', {
    id,
  });

export const apiGetProfile = async (): Promise<ApiProfileJSON> => {
  const useLegacyProfile = !mastodonApiVersion || mastodonApiVersion < 9;
  if (useLegacyProfile) {
    let hasFeaturedTagsSupport = true;
    const [credentials, featuredTags] = await Promise.all([
      apiRequestGet<any>('v1/accounts/verify_credentials'),
      apiRequestGet<any>('v1/featured_tags').catch(() => {
        hasFeaturedTagsSupport = false;
        return [];
      }),
    ]);

    return {
      id: credentials.id,
      display_name: credentials.display_name,
      note: credentials.source?.note ?? credentials.note,
      fields: credentials.source?.fields ?? credentials.fields ?? [],
      avatar: credentials.avatar,
      avatar_static: credentials.avatar_static,
      avatar_description: credentials.source?.avatar_description ?? '',
      header: credentials.header,
      header_static: credentials.header_static,
      header_description: credentials.source?.header_description ?? '',
      locked: credentials.locked,
      bot: credentials.bot,
      hide_collections: credentials.source?.hide_collections ?? false,
      discoverable: credentials.discoverable,
      indexable: credentials.indexable,
      show_media: credentials.source?.show_media ?? true,
      show_media_replies: credentials.source?.show_media_replies ?? false,
      show_featured: credentials.source?.show_featured ?? true,
      attribution_domains: credentials.source?.attribution_domains ?? [],
      featured_tags: featuredTags,
      has_featured_tags_support: hasFeaturedTagsSupport,
    };
  }

  try {
    const profile = await apiRequestGet<ApiProfileJSON>('v1/profile');
    let hasFeaturedTagsSupport = true;
    await apiRequestGet<any>('v1/featured_tags').catch(() => {
      hasFeaturedTagsSupport = false;
    });
    return {
      ...profile,
      has_featured_tags_support: hasFeaturedTagsSupport,
    };
  } catch (error: any) {
    if (error?.response?.status === 404) {
      let hasFeaturedTagsSupport = true;
      const [credentials, featuredTags] = await Promise.all([
        apiRequestGet<any>('v1/accounts/verify_credentials'),
        apiRequestGet<any>('v1/featured_tags').catch(() => {
          hasFeaturedTagsSupport = false;
          return [];
        }),
      ]);

      return {
        id: credentials.id,
        display_name: credentials.display_name,
        note: credentials.source?.note ?? credentials.note,
        fields: credentials.source?.fields ?? credentials.fields ?? [],
        avatar: credentials.avatar,
        avatar_static: credentials.avatar_static,
        avatar_description: credentials.source?.avatar_description ?? '',
        header: credentials.header,
        header_static: credentials.header_static,
        header_description: credentials.source?.header_description ?? '',
        locked: credentials.locked,
        bot: credentials.bot,
        hide_collections: credentials.source?.hide_collections ?? false,
        discoverable: credentials.discoverable,
        indexable: credentials.indexable,
        show_media: credentials.source?.show_media ?? true,
        show_media_replies: credentials.source?.show_media_replies ?? false,
        show_featured: credentials.source?.show_featured ?? true,
        attribution_domains: credentials.source?.attribution_domains ?? [],
        featured_tags: featuredTags,
        has_featured_tags_support: hasFeaturedTagsSupport,
      };
    }
    throw error;
  }
};

export const apiPatchProfile = async (params: ApiProfileUpdateParams | FormData): Promise<ApiProfileJSON> => {
  const useLegacyProfile = !mastodonApiVersion || mastodonApiVersion < 9;
  if (useLegacyProfile) {
    await apiRequestPatch<any>('v1/accounts/update_credentials', params);
    return await apiGetProfile();
  }

  try {
    return await apiRequestPatch<ApiProfileJSON>('v1/profile', params);
  } catch (error: any) {
    if (error?.response?.status === 404) {
      await apiRequestPatch<any>('v1/accounts/update_credentials', params);
      return await apiGetProfile();
    }
    throw error;
  }
};

export const apiDeleteProfileAvatar = async () => {
  const useLegacyProfile = !mastodonApiVersion || mastodonApiVersion < 9;
  if (useLegacyProfile) {
    const formData = new FormData();
    formData.append('avatar', '');
    return await apiRequestPatch<any>('v1/accounts/update_credentials', formData);
  }

  try {
    return await apiRequestDelete('v1/profile/avatar');
  } catch (error: any) {
    if (error?.response?.status === 404) {
      const formData = new FormData();
      formData.append('avatar', '');
      return await apiRequestPatch<any>('v1/accounts/update_credentials', formData);
    }
    throw error;
  }
};

export const apiDeleteProfileHeader = async () => {
  const useLegacyProfile = !mastodonApiVersion || mastodonApiVersion < 9;
  if (useLegacyProfile) {
    const formData = new FormData();
    formData.append('header', '');
    return await apiRequestPatch<any>('v1/accounts/update_credentials', formData);
  }

  try {
    return await apiRequestDelete('v1/profile/header');
  } catch (error: any) {
    if (error?.response?.status === 404) {
      const formData = new FormData();
      formData.append('header', '');
      return await apiRequestPatch<any>('v1/accounts/update_credentials', formData);
    }
    throw error;
  }
};

export const apiSubscribeByEmail = (id: string, email: string) =>
  apiRequestPost(`v1/accounts/${id}/email_subscriptions`, { email });
